import React from 'react'
import { Table, TableColumnsType, Tag } from 'antd'
import { IMovementRS } from '../../../models/salesModel'
import { textNumberFormater } from '../../../utils'
import { calculateSummary, getMovementPaymentLabel } from '../../../utils/reportUtils'
import { exportToExcel, TExcelColumn } from '../../../utils/excelExport'
import ReportSummaryCards, { TSummaryCard } from './ReportSummaryCards'
import { useRegister } from '../../../contexts/RegisterContext'
import {
  DollarOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
  TagOutlined,
  RiseOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'

type SalesByPeriodReportProps = {
  data: IMovementRS[]
  loading?: boolean
}

function SalesByPeriodReport({ data, loading }: Readonly<SalesByPeriodReportProps>) {
  const { register } = useRegister()
  const summary = calculateSummary(data)

  const cards: TSummaryCard[] = [
    {
      title: 'Total Vendido',
      value: summary.totalAmount,
      format: 'money',
      color: '#52c41a',
      prefix: <DollarOutlined />
    },
    {
      title: 'Facturas',
      value: summary.totalSales,
      format: 'number',
      color: '#1890ff',
      prefix: <FileTextOutlined />
    },
    {
      title: 'Promedio por Venta',
      value: summary.averagePerSale,
      format: 'money',
      color: '#722ed1',
      prefix: <RiseOutlined />
    },
    {
      title: 'Total Impuestos',
      value: summary.totalTaxes,
      format: 'money',
      color: '#fa8c16',
      prefix: <TagOutlined />
    },
    {
      title: 'Total Descuentos',
      value: summary.totalDiscounts,
      format: 'money',
      color: '#eb2f96',
      prefix: <TagOutlined />
    },
    {
      title: 'Total Ítems',
      value: summary.totalItems,
      format: 'number',
      color: '#13c2c2',
      prefix: <ShoppingCartOutlined />
    }
  ]

  const columns: TableColumnsType = [
    { title: '#', dataIndex: 'register', width: 50 },
    { title: 'Documento', dataIndex: 'document_number', width: 140 },
    {
      title: 'Tipo',
      dataIndex: 'document_type',
      width: 130,
      render: (text) => {
        const labels: Record<string, { label: string; color: string }> = {
          'SALE_INVOICE': { label: 'Factura', color: 'blue' },
          'CREDIT_NOTE': { label: 'Nota Crédito', color: 'orange' },
          'DEBIT_NOTE': { label: 'Nota Débito', color: 'purple' }
        }
        const config = labels[text] || { label: text, color: 'default' }
        return <Tag color={config.color}>{config.label}</Tag>
      },
      filters: [
        { text: 'Factura', value: 'SALE_INVOICE' },
        { text: 'Nota Crédito', value: 'CREDIT_NOTE' },
        { text: 'Nota Débito', value: 'DEBIT_NOTE' }
      ],
      onFilter: (value, record: any) => record.document_type === value
    },
    {
      title: 'Fecha',
      dataIndex: 'updated_at',
      width: 160,
      render: (text) => dayjs(text).format('DD/MM/YYYY hh:mm A'),
      sorter: (a: any, b: any) => new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime()
    },
    { title: 'Ítems', dataIndex: 'items', width: 70, align: 'center' },
    {
      title: 'Cliente',
      dataIndex: 'contact_name',
      width: 180,
      render: (text) => text || 'Consumidor Final'
    },
    { title: 'Vendedor', dataIndex: 'created_by_user_name', width: 160 },
    {
      title: 'Subtotal',
      dataIndex: 'total_amount',
      width: 120,
      align: 'right',
      render: (text) => <span className="numeric-values-data-table">{textNumberFormater(text)}</span>,
      sorter: (a: any, b: any) => a.total_amount - b.total_amount
    },
    {
      title: 'Impuestos',
      dataIndex: 'total_tax_amount',
      width: 110,
      align: 'right',
      render: (text) => <span className="numeric-values-data-table">{textNumberFormater(text || 0)}</span>
    },
    {
      title: 'Descuentos',
      dataIndex: 'total_discount_amount',
      width: 110,
      align: 'right',
      render: (text) => <span className="numeric-values-data-table">{textNumberFormater(text || 0)}</span>
    },
    {
      title: 'Total',
      dataIndex: 'total_amount_net',
      width: 120,
      align: 'right',
      render: (text, record: any) => (
        <span className="numeric-values-data-table" style={{ fontWeight: 600 }}>
          {textNumberFormater(text || record.total_amount || 0)}
        </span>
      ),
      sorter: (a: any, b: any) => (a.total_amount_net || a.total_amount) - (b.total_amount_net || b.total_amount)
    },
    {
      title: 'Pago',
      dataIndex: 'payment_label',
      width: 110,
      render: (text: string, record: any) => {
        const isCredit = text === 'Crédito' || record.payment_term === 'CREDIT'
        return (
          <Tag color={isCredit ? 'volcano' : 'green'}>
            {text}
          </Tag>
        )
      },
      filters: [
        { text: 'Contado', value: 'Contado' },
        { text: 'Crédito', value: 'Crédito' }
      ],
      onFilter: (value, record: any) => (record.payment_label || '').includes(value as string)
    }
  ]

  const tableData = data.map((el, i) => ({
    ...el,
    register: i + 1,
    key: el.uid || i,
    payment_label: getMovementPaymentLabel(el, register.paymentOptions)
  }))

  return (
    <div>
      <ReportSummaryCards cards={cards} loading={loading} />
      <Table
        columns={columns}
        dataSource={tableData}
        pagination={tableData.length > 15 ? { pageSize: 15, showSizeChanger: true, showTotal: (total) => `Total ${total} registros` } : false}
        scroll={{ x: 1400 }}
        size="small"
        loading={loading}
        summary={() => {
          if (tableData.length === 0) return null
          return (
            <Table.Summary fixed>
              <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 600 }}>
                <Table.Summary.Cell index={0} colSpan={7}>TOTALES</Table.Summary.Cell>
                <Table.Summary.Cell index={7} align="right">
                  <span className="numeric-values-data-table">{textNumberFormater(summary.totalAmount - summary.totalTaxes + summary.totalDiscounts)}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8} align="right">
                  <span className="numeric-values-data-table">{textNumberFormater(summary.totalTaxes)}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={9} align="right">
                  <span className="numeric-values-data-table">{textNumberFormater(summary.totalDiscounts)}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={10} align="right">
                  <span className="numeric-values-data-table" style={{ fontWeight: 700, color: '#52c41a' }}>
                    {textNumberFormater(summary.totalAmount)}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={11}></Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )
        }}
      />
    </div>
  )
}

export const periodExcelColumns: TExcelColumn[] = [
  { header: '#', key: 'register', width: 6 },
  { header: 'Documento', key: 'document_number', width: 20 },
  { header: 'Tipo', key: 'document_type_label', width: 15 },
  { header: 'Fecha', key: 'date_formatted', width: 22 },
  { header: 'Ítems', key: 'items', width: 8, format: 'number' },
  { header: 'Cliente', key: 'contact_name_display', width: 25 },
  { header: 'Vendedor', key: 'created_by_user_name', width: 22 },
  { header: 'Subtotal', key: 'total_amount', width: 15, format: 'money' },
  { header: 'Impuestos', key: 'total_tax_amount', width: 15, format: 'money' },
  { header: 'Descuentos', key: 'total_discount_amount', width: 15, format: 'money' },
  { header: 'Total', key: 'total_amount_net_display', width: 15, format: 'money' },
  { header: 'Método Pago', key: 'payment_term_label', width: 15 }
]

export function getPeriodExcelData(data: IMovementRS[], paymentOptions?: any[]) {
  const typeLabels: Record<string, string> = {
    'SALE_INVOICE': 'Factura',
    'CREDIT_NOTE': 'Nota Crédito',
    'DEBIT_NOTE': 'Nota Débito'
  }
  return data.map((el, i) => ({
    ...el,
    register: i + 1,
    document_type_label: typeLabels[el.document_type] || el.document_type,
    date_formatted: dayjs(el.updated_at).format('DD/MM/YYYY hh:mm A'),
    contact_name_display: el.contact_name || 'Consumidor Final',
    total_amount_net_display: el.total_amount_net || el.total_amount || 0,
    payment_term_label: getMovementPaymentLabel(el, paymentOptions)
  }))
}

export default SalesByPeriodReport
